import {KatButton, KatDropdown, KatIcon, KatPopover, KatTextarea} from "@amzn/katal-react";
import * as React from "react";
import {useRef} from "react";
import reviewDecisionStore from "src/app/store/ReviewDecisionStore";
import reviewEventBus from "src/app/eventbus/ReviewEventBus";
import {ReOpenFor, ReviewEvent, ReviewResult, ReviewStatus, ReviewType, Stage} from "src/app/constant/Enums";
import {DropDownOption} from "src/app/model/DropDownOption";
import {GenericValue} from "src/app/model/GenricValue";
import {StringUtil} from "src/app/util/StringUtil";
import userInfoStore from "src/app/store/UserInfoStore";
import {STAGE} from "src/app/config/EnvConfig";
import {isAppealEnable} from "src/app/constant/Constants";
import {CASE_DECISION_BUTTON_PAIR_CONFIG, DEFAULT_DECISION_BUTTON_PAIR} from "src/app/config/BtnConfig";

interface ReviewDecisionProps {
    slot: string;
    reasonCodes: DropDownOption[];
    reviewType: string;
    reviewStatus: string;
    reviewResult: string;
    reOpenFor?: string;
    lastAuditor?: string;
    caseSubType: string;
    decisionDetail?: { [key: string]: GenericValue };
}

const regularActionResultSet = new Set([ReviewResult.OFFBOARDED.toString(), ReviewResult.INACTIVATED.toString()]);

const ReviewDecision = (props: ReviewDecisionProps) => {
    const reasonCodeRef = useRef<any>(null);
    const commentRef = useRef<any>(null);

    const btnPair = CASE_DECISION_BUTTON_PAIR_CONFIG[props.caseSubType] == undefined ? DEFAULT_DECISION_BUTTON_PAIR :
        CASE_DECISION_BUTTON_PAIR_CONFIG[props.caseSubType];

    const submitDecision = (reviewResult: ReviewResult, reOpenFor: string | undefined) => {
        const reasonCode = reasonCodeRef.current?.value;
        const comment = commentRef.current?.value;
        const reviewStatus = ReviewStatus.COMPLETED;
        const {auditorInputs} = reviewDecisionStore.value;
        const newValue = {
            reason: reasonCode,
            comment: comment,
            reOpenFor: reOpenFor,
            reviewResult: reviewResult,
            reviewStatus: reviewStatus,
            auditorInputs: auditorInputs
        }
        reviewDecisionStore.updateValue(newValue);
        reviewEventBus.emit(ReviewEvent.SUBMIT_REVIEW_DECISION, true);
    }

    const submitAction = () => {
        submitDecision(btnPair.actionResult, undefined);
    }

    const noAction = () => {
        submitDecision(ReviewResult.NO_ACTION, undefined);
    }

    const reinstate = () => {
        submitDecision(ReviewResult.RE_INSTATED, undefined);
    }

    const noReInstate = () => {
        submitDecision(ReviewResult.NO_RE_INSTATED, undefined);
    }

    const reopen = (reOpenFor: string) => {
        if (Stage.PROD == STAGE.toLowerCase() && userInfoStore.value == props.lastAuditor) {
            alert(userInfoStore.value + " is not allowed to reopen review since he/she made the previous decision!");
            return;
        }
        submitDecision(ReviewResult.RE_OPENED, reOpenFor);
    }

    const onReturn = () => {
        reviewEventBus.emit(ReviewEvent.CLOSE_REVIEW, true);
    }

    const onReasonSelect = () => {
        reasonCodeRef.current.showClear = true;
    }

    function renderPrimaryAuditDecision() {
        return <>
            {
                (ReviewStatus.PENDING == props.reviewStatus) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     showClear={false}
                                     onChange={onReasonSelect}
                                     value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} onClick={submitAction} className='decision-button amazon-orange'
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} onClick={noAction} className='decision-button'
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {
                (ReviewStatus.COMPLETED == props.reviewStatus) && regularActionResultSet.has(props.reviewResult) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     disabled={true} value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} onClick={submitAction} className='decision-button amazon-orange'
                                   disabled={true}
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} onClick={noAction} className='decision-button' disabled={true}
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/* reviewStatus == 'COMPLETE' && reviewResult == 'NO_ACTION' && !hasBeenReOpened-> Reopen to Offboard*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.NO_ACTION == props.reviewResult
                    && !hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={btnPair.noActionReopenBtnTxt} onClick={() => reopen(ReOpenFor.RECTIFYING)}
                                   className='single-button amazon-orange'
                                   data-testid="reopen-to-offboard-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*  reviewStatus == 'COMPLETE' && ReviewResult.RE_OPENED -> disable reopen button*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.RE_OPENED == props.reviewResult) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={btnPair.noActionReopenBtnTxt}
                                   className='single-button amazon-orange'
                                   disabled={true}
                                   data-testid="reopen-to-offboard-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }
        </>;
    }

    function renderReOpenAuditDecision() {
        return <>
            {/* reviewStatus == 'PENDING' && reOpenFor == 'RECTIFYING' -> offboard DP, no action, reason code*/}
            {
                (ReviewStatus.PENDING == props.reviewStatus && ReOpenFor.RECTIFYING == props.reOpenFor) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     showClear={false}
                                     onChange={onReasonSelect}
                                     value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} onClick={submitAction} className='decision-button amazon-orange'
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} onClick={noAction} className='decision-button'
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/* reviewStatus == 'PENDING' && reOpenFor == 'APPEAL' -> Do not reinstate DP, Reinstate action, reason code*/}
            {
                (ReviewStatus.PENDING == props.reviewStatus && ReOpenFor.APPEAL == props.reOpenFor) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reinstate DP"} onClick={reinstate} className='single-button amazon-orange'
                                   data-testid="reinstate-btn"></KatButton>
                        <KatButton label={"DO not reinstate DP"} onClick={noReInstate} className='single-button'
                                   data-testid="not-reinstate-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/* reviewStatus == 'COMPLETE' && reOpenFor == 'RECTIFYING' -> disable button*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReOpenFor.RECTIFYING == props.reOpenFor) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     disabled={true}
                                     value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} className='decision-button' disabled={true}
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} className='decision-button' disabled={true}
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/* reviewStatus == 'COMPLETE' && reOpenFor == 'APPEAL' -> disable buttons*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReOpenFor.APPEAL == props.reOpenFor) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reinstate DP"} className='single-button' disabled={true}
                                   data-testid="reinstate-btn"></KatButton>
                        <KatButton label={"Do not Reinstate DP"} className='single-button' disabled={true}
                                   data-testid="not-reinstate-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }
        </>;
    }

    function hasBeenReOpened() {
        return !StringUtil.isNullOrEmpty(props.reOpenFor);
    }

    function renderSecondaryAuditDecision() {
        return <>
            {
                (ReviewStatus.PENDING == props.reviewStatus) && !hasBeenReOpened() &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     showClear={false}
                                     onChange={onReasonSelect}
                                     value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} onClick={submitAction} className='decision-button amazon-orange'
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} onClick={noAction} className='decision-button'
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/* reviewStatus == 'PENDING' && reOpenFor == 'RECTIFYING' -> offboard DP, no action, reason code*/}
            {
                (ReviewStatus.PENDING == props.reviewStatus) && ReOpenFor.RECTIFYING == props.reOpenFor &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     showClear={false}
                                     onChange={onReasonSelect}
                                     value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} onClick={submitAction} className='decision-button amazon-orange'
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} onClick={noAction} className='decision-button'
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*  reviewStatus == 'PENDING' && reOpenFor == 'APPEAL' -> Do not reinstate DP, Reinstate action*/}
            {
                (ReviewStatus.PENDING == props.reviewStatus) && ReOpenFor.APPEAL == props.reOpenFor &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reinstate DP"} onClick={reinstate} className='single-button amazon-orange'
                                   data-testid="reinstate-btn"></KatButton>
                        <KatButton label={"Do not Reinstate DP"} onClick={noReInstate} className='single-button'
                                   data-testid="not-reinstate-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*   reviewStatus == 'COMPLETE' && reviewResult == 'NO_ACTION' && !hasBeenReOpened-> Reopen to Offboard*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.NO_ACTION == props.reviewResult &&
                    !hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={false}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={btnPair.noActionReopenBtnTxt} onClick={() => reopen(ReOpenFor.RECTIFYING)}
                                   className='single-button amazon-orange'
                                   data-testid="reopen-to-offboard-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*   reviewStatus == 'COMPLETE' && reviewResult == 'OFFBOARDED' && !hasBeenReOpened-> Reopen for Appeal*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && regularActionResultSet.has(props.reviewResult) &&
                    !hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={!isAppealEnable()}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reopen for Appeal"} onClick={() => reopen(ReOpenFor.APPEAL)}
                                   className='single-button amazon-orange'
                                   disabled={!isAppealEnable()}
                                   data-testid="reopen-to-appeal-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.NO_ACTION == props.reviewResult &&
                    hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     disabled={true} value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} className='decision-button'
                                   disabled={true}
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} className='decision-button' disabled={true}
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*    reviewType == 'SECONDARY_AUDIT' && reviewStatus == 'COMPLETE' && reviewResult == OFFBOARDED && hasBeenReOpened == true
             -> disable buttons*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && regularActionResultSet.has(props.reviewResult) &&
                    hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatDropdown placeholder={'Reason'} options={props.reasonCodes} state={'error'}
                                     ref={reasonCodeRef}
                                     disabled={true} value={props.decisionDetail?.reasonCode?.jsonVal}
                                     data-testid="reason-code" className='reason'></KatDropdown>
                        <KatButton label={btnPair.actionBtnTxt} className='decision-button'
                                   disabled={true}
                                   data-testid="approve-btn"></KatButton>
                        <KatButton label={btnPair.noActionBtnTxt} className='decision-button' disabled={true}
                                   data-testid="reject-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*    reviewType == 'SECONDARY_AUDIT' && reviewStatus == 'COMPLETE' && reviewResult == RE_INSTATED && hasBeenReOpened == true
             -> disable buttons*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.RE_INSTATED == props.reviewResult &&
                    hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reinstate DP"} className='single-button'
                                   disabled={true}
                                   data-testid="reinstate-btn"></KatButton>
                        <KatButton label={"Do not Reinstate DP"} className='single-button' disabled={true}
                                   data-testid="not-reinstate-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/*    reviewType == 'SECONDARY_AUDIT' && reviewStatus == 'COMPLETE' && reviewResult == NO_INSTATED && hasBeenReOpened == true
             -> disable buttons*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.NO_RE_INSTATED == props.reviewResult &&
                    hasBeenReOpened()) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reinstate DP"} className='single-button'
                                   disabled={true}
                                   data-testid="reinstate-btn"></KatButton>
                        <KatButton label={"Do not Reinstate DP"} className='single-button' disabled={true}
                                   data-testid="not-reinstate-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }
            {/*This happens when auditor reopen the review*/}
            {/* reviewStatus == 'COMPLETE' && ReOpenFor.RECTIFYING && ReviewResult.RE_OPENED -> disable reopen button*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.RE_OPENED == props.reviewResult 
                    && ReOpenFor.RECTIFYING == props.reOpenFor) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={btnPair.noActionReopenBtnTxt}
                                   className='single-button amazon-orange'
                                   disabled={true}
                                   data-testid="reopen-to-offboard-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }

            {/* reviewStatus == 'COMPLETE' &&  ReOpenFor.APPEAL && ReviewResult.RE_OPENED -> disable reopen button*/}
            {
                (ReviewStatus.COMPLETED == props.reviewStatus && ReviewResult.RE_OPENED == props.reviewResult
                    && ReOpenFor.APPEAL == props.reOpenFor) &&
                <>
                    <KatTextarea className='comment' label={'Auditor Remarks'} data-testid="comment"
                                 disabled={true}
                                 value={props.decisionDetail?.comment?.jsonVal}
                                 ref={commentRef}></KatTextarea>
                    <div className='inline-display decision-button-div center'>
                        <KatButton label={"Reopen for Appeal"}
                                   className='single-button amazon-orange'
                                   disabled={true}
                                   data-testid="reopen-to-appeal-btn"></KatButton>
                        <KatPopover></KatPopover>
                    </div>
                </>
            }
        </>;
    }

    return (
        <div slot={props.slot} className="katal container inline-display">
            <KatIcon name={"keyboard_arrow_left"} size={"xlarge"} onClick={onReturn} data-testid="return-icon"
                     className="hover-pointer"></KatIcon>
            {ReviewType.PRIMARY_AUDIT == props.reviewType && renderPrimaryAuditDecision()}
            {ReviewType.REOPEN_AUDIT == props.reviewType && renderReOpenAuditDecision()}
            {ReviewType.SECONDARY_AUDIT == props.reviewType && renderSecondaryAuditDecision()}
        </div>
    );
}

export default ReviewDecision;